import React, { useState, useEffect } from 'react';
import { restaurantAPI } from '../../services/api';

// Flutter App Theme Colors (exact match)
const theme = {
  primaryColor: '#FF6961',
  primaryLight: '#FF8A84',
  primaryDark: '#E6524A',
  backgroundColor: '#F8FAFC',
  surfaceColor: '#FFFFFF',
  textPrimary: '#0F172A',
  textSecondary: '#64748B',
  successColor: '#22C55E',
  warningColor: '#F59E0B',
  errorColor: '#EF4444',
  infoColor: '#3B82F6',
};

function BankDetails() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const [verifiedAt, setVerifiedAt] = useState(null);
  const [verificationError, setVerificationError] = useState(null);
  const [remainingAttempts, setRemainingAttempts] = useState(3);
  const [nameFromBank, setNameFromBank] = useState(null);
  const [hasBankDetails, setHasBankDetails] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  const [formData, setFormData] = useState({
    accountNumber: '',
    confirmAccountNumber: '',
    ifscCode: '',
    accountHolderName: '',
    bankName: '',
    branchName: ''
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    loadBankStatus();
  }, []);

  const loadBankStatus = async () => {
    setLoading(true);
    try {
      const response = await restaurantAPI.getBankVerificationStatus();
      const data = response.data.data || response.data;

      setIsVerified(data.verified || false);
      setVerifiedAt(data.verifiedAt ? new Date(data.verifiedAt) : null);
      setVerificationError(data.verificationError || null);
      setRemainingAttempts(data.remainingAttempts ?? 3);
      setNameFromBank(data.nameFromBank || null);

      if (data.accountNumber) {
        setHasBankDetails(true);
        setFormData({
          accountNumber: data.accountNumber || '',
          confirmAccountNumber: data.accountNumber || '',
          ifscCode: data.ifscCode || '',
          accountHolderName: data.accountHolderName || '',
          bankName: data.bankName || '',
          branchName: data.branchName || ''
        });
      }
    } catch (error) {
      console.error('Failed to load bank status:', error);
    } finally {
      setLoading(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.accountNumber) {
      newErrors.accountNumber = 'Account number is required';
    } else if (formData.accountNumber.length < 9 || formData.accountNumber.length > 18) {
      newErrors.accountNumber = 'Invalid account number (9-18 digits)';
    }

    if (formData.accountNumber !== formData.confirmAccountNumber) {
      newErrors.confirmAccountNumber = 'Account numbers do not match';
    }

    if (!formData.ifscCode) {
      newErrors.ifscCode = 'IFSC code is required';
    } else if (!/^[A-Z]{4}0[A-Z0-9]{6}$/.test(formData.ifscCode.toUpperCase())) {
      newErrors.ifscCode = 'Invalid IFSC code format';
    }

    if (!formData.accountHolderName) {
      newErrors.accountHolderName = 'Account holder name is required';
    }

    if (!formData.bankName) {
      newErrors.bankName = 'Bank name is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // ==========================================
  // BANK VERIFICATION - DISABLED (Manual Payout Mode)
  // ==========================================
  const handleVerifyBankAccount = async () => {
    // Bank verification is disabled in manual payout mode
    alert('Bank verification is currently disabled. Your bank details have been saved and payouts will be processed manually by the admin team.');
  };

  const handleSaveAndVerify = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setSaving(true);
    try {
      const response = await restaurantAPI.updateAndVerifyBank({
        accountNumber: formData.accountNumber.trim(),
        ifscCode: formData.ifscCode.trim().toUpperCase(),
        accountHolderName: formData.accountHolderName.trim(),
        bankName: formData.bankName.trim(),
        branchName: formData.branchName.trim() || undefined
      });

      const data = response.data.data || response.data;

      setHasBankDetails(true);
      setIsEditing(false);
      setIsVerified(data.verified || false);
      setNameFromBank(data.nameFromBank || null);
      setRemainingAttempts(data.remainingAttempts ?? 0);

      if (data.verified) {
        setVerifiedAt(new Date());
        setVerificationError(null);
        alert('Bank details saved and verified!');
      } else {
        setVerificationError(data.message || null);
        alert('Bank details saved. ' + (data.message || ''));
      }
    } catch (error) {
      alert('Failed to save bank details: ' + (error.response?.data?.message || error.message));
    } finally {
      setSaving(false);
    }
  };

  const maskAccountNumber = (accountNumber) => {
    if (!accountNumber || accountNumber.length <= 4) return accountNumber;
    return 'XXXX' + accountNumber.substring(accountNumber.length - 4);
  };

  const formatDate = (date) => {
    if (!date) return '';
    return `${date.getDate()}/${date.getMonth() + 1}/${date.getFullYear()}`;
  };

  const getStatusInfo = () => {
    if (isVerified) {
      return {
        color: theme.successColor,
        bgColor: 'rgba(34, 197, 94, 0.1)',
        borderColor: 'rgba(34, 197, 94, 0.3)',
        icon: '✓',
        text: 'Verified',
        description: verifiedAt ? `Verified on ${formatDate(verifiedAt)}` : 'Bank account verified successfully'
      };
    } else if (verificationError) {
      return {
        color: theme.errorColor,
        bgColor: 'rgba(239, 68, 68, 0.1)',
        borderColor: 'rgba(239, 68, 68, 0.3)',
        icon: '✕',
        text: 'Verification Failed',
        description: verificationError
      };
    } else if (!hasBankDetails) {
      return {
        color: theme.textSecondary,
        bgColor: 'rgba(100, 116, 139, 0.1)',
        borderColor: 'rgba(100, 116, 139, 0.3)',
        icon: '🏦',
        text: 'Not Added',
        description: 'Add your bank details to receive payouts'
      };
    } else {
      // Bank verification disabled - show informational status
      return {
        color: theme.infoColor,
        bgColor: 'rgba(59, 130, 246, 0.1)',
        borderColor: 'rgba(59, 130, 246, 0.3)',
        icon: 'ℹ️',
        text: 'Bank Details Saved',
        description: 'Payouts are processed manually by the admin team'
      };
    }
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '60vh'
      }}>
        <div>Loading bank details...</div>
      </div>
    );
  }

  const statusInfo = getStatusInfo();

  return (
    <div style={{ background: theme.backgroundColor, minHeight: '100vh', padding: '24px' }}>
      <div style={{ maxWidth: '600px', margin: '0 auto' }}>
        {/* Header */}
        <h1 style={{
          fontSize: '24px',
          fontWeight: '700',
          color: theme.textPrimary,
          marginBottom: '24px'
        }}>
          Bank Details
        </h1>

        {/* Verification Status Card */}
        <div style={{
          padding: '16px',
          background: statusInfo.bgColor,
          borderRadius: '12px',
          border: `1px solid ${statusInfo.borderColor}`,
          marginBottom: '20px'
        }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
            <div style={{
              fontSize: '28px',
              color: statusInfo.color
            }}>
              {statusInfo.icon}
            </div>
            <div style={{ flex: 1 }}>
              <div style={{
                fontSize: '18px',
                fontWeight: '700',
                color: statusInfo.color,
                marginBottom: '4px'
              }}>
                {statusInfo.text}
              </div>
              <div style={{
                fontSize: '14px',
                color: theme.textSecondary
              }}>
                {statusInfo.description}
              </div>
            </div>
          </div>

          {/* Name from Bank */}
          {nameFromBank && isVerified && (
            <div style={{
              marginTop: '12px',
              padding: '12px',
              background: 'white',
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <span style={{ color: theme.primaryColor }}>👤</span>
              <div>
                <div style={{ fontSize: '12px', color: theme.textSecondary }}>
                  Name from Bank
                </div>
                <div style={{ fontSize: '14px', fontWeight: '500' }}>
                  {nameFromBank}
                </div>
              </div>
            </div>
          )}

          {/* Verify Button - HIDDEN (Manual Payout Mode) */}
          {/* Bank verification is disabled. Payouts processed manually. */}
        </div>

        {/* Bank Details Card or Form */}
        {hasBankDetails && !isEditing ? (
          // View Mode - Show Bank Details
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '16px',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '16px'
            }}>
              <h2 style={{ fontSize: '18px', fontWeight: '700', margin: 0 }}>
                Bank Account
              </h2>
              {!isVerified && (
                <button
                  onClick={() => setIsEditing(true)}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: theme.primaryColor,
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '500',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}
                >
                  ✏️ Edit
                </button>
              )}
            </div>

            <div style={{ display: 'grid', gap: '12px' }}>
              <DetailRow label="Account Number" value={maskAccountNumber(formData.accountNumber)} />
              <DetailRow label="IFSC Code" value={formData.ifscCode.toUpperCase()} />
              <DetailRow label="Account Holder" value={formData.accountHolderName} />
              <DetailRow label="Bank Name" value={formData.bankName} />
              {formData.branchName && (
                <DetailRow label="Branch" value={formData.branchName} />
              )}
            </div>

            {isVerified && (
              <div style={{
                marginTop: '12px',
                padding: '12px',
                background: 'rgba(34, 197, 94, 0.1)',
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <span>🔒</span>
                <span style={{ fontSize: '12px', color: theme.successColor }}>
                  Verified accounts cannot be edited. Contact support for changes.
                </span>
              </div>
            )}
          </div>
        ) : (
          // Edit/Add Mode - Show Form
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '16px',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '20px'
            }}>
              <h2 style={{ fontSize: '18px', fontWeight: '700', margin: 0 }}>
                {hasBankDetails ? 'Edit Bank Details' : 'Add Bank Details'}
              </h2>
              {isEditing && (
                <button
                  onClick={() => setIsEditing(false)}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: theme.textSecondary,
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                >
                  Cancel
                </button>
              )}
            </div>

            <form onSubmit={handleSaveAndVerify}>
              <FormField
                label="Account Number"
                value={formData.accountNumber}
                onChange={(e) => setFormData({ ...formData, accountNumber: e.target.value.replace(/\D/g, '') })}
                placeholder="Enter account number"
                error={errors.accountNumber}
                type="text"
                inputMode="numeric"
              />

              <FormField
                label="Confirm Account Number"
                value={formData.confirmAccountNumber}
                onChange={(e) => setFormData({ ...formData, confirmAccountNumber: e.target.value.replace(/\D/g, '') })}
                placeholder="Re-enter account number"
                error={errors.confirmAccountNumber}
                type="text"
                inputMode="numeric"
              />

              <FormField
                label="IFSC Code"
                value={formData.ifscCode}
                onChange={(e) => setFormData({ ...formData, ifscCode: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 11) })}
                placeholder="e.g., SBIN0001234"
                error={errors.ifscCode}
              />

              <FormField
                label="Account Holder Name"
                value={formData.accountHolderName}
                onChange={(e) => setFormData({ ...formData, accountHolderName: e.target.value })}
                placeholder="Name as per bank records"
                error={errors.accountHolderName}
              />

              <FormField
                label="Bank Name"
                value={formData.bankName}
                onChange={(e) => setFormData({ ...formData, bankName: e.target.value })}
                placeholder="e.g., State Bank of India"
                error={errors.bankName}
              />

              <FormField
                label="Branch Name (Optional)"
                value={formData.branchName}
                onChange={(e) => setFormData({ ...formData, branchName: e.target.value })}
                placeholder="e.g., Main Branch, Mumbai"
              />

              <button
                type="submit"
                disabled={saving}
                style={{
                  width: '100%',
                  marginTop: '16px',
                  padding: '14px',
                  background: saving ? '#ccc' : theme.primaryColor,
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: saving ? 'not-allowed' : 'pointer'
                }}
              >
                {saving ? 'Saving...' : 'Save Bank Details'}
              </button>

              <div style={{
                marginTop: '12px',
                textAlign: 'center',
                fontSize: '12px',
                color: theme.textSecondary
              }}>
                Payouts will be processed manually by the admin team
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}

// Helper Components
function DetailRow({ label, value }) {
  return (
    <div style={{ display: 'flex' }}>
      <div style={{
        width: '120px',
        fontSize: '14px',
        color: '#64748B',
        flexShrink: 0
      }}>
        {label}
      </div>
      <div style={{
        fontSize: '14px',
        fontWeight: '500',
        color: '#0F172A'
      }}>
        {value}
      </div>
    </div>
  );
}

function FormField({ label, value, onChange, placeholder, error, type = 'text', inputMode }) {
  return (
    <div style={{ marginBottom: '16px' }}>
      <label style={{
        display: 'block',
        fontSize: '14px',
        fontWeight: '500',
        color: '#0F172A',
        marginBottom: '8px'
      }}>
        {label}
      </label>
      <input
        type={type}
        inputMode={inputMode}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        style={{
          width: '100%',
          padding: '12px 16px',
          fontSize: '14px',
          border: error ? '1px solid #EF4444' : '1px solid #E5E7EB',
          borderRadius: '8px',
          background: '#F8FAFC',
          outline: 'none',
          boxSizing: 'border-box'
        }}
      />
      {error && (
        <div style={{
          marginTop: '4px',
          fontSize: '12px',
          color: '#EF4444'
        }}>
          {error}
        </div>
      )}
    </div>
  );
}

export default BankDetails;
